import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { ConfiguratorContext } from "../context";
import { CustomerWarrantyDto } from "../api/models";
import { useForm } from "antd/lib/form/Form";
import useCustomers from "../swr/useCustomers";

const moneyInputFormatter = (value: string | undefined) =>
  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const moneyInputParser = (value: string | undefined) =>
  value!.replace(/\$\s?|(,*)/g, "");

const percentInputFormatter = (value: string | undefined) => {
  const trimZero = (val: string | undefined) => {
    const rslt = val?.replace(/^0+/, "").replace(/\.*0+%$/, "%") || "";
    return rslt === "%" ? "0%" : rslt;
  };
  return trimZero(((parseFloat(String(value)) || 0.0) * 100).toFixed(4) + "%");
};
const percentInputParser = (value: string | undefined) =>
  String(parseFloat(value!.replace(/\%\s?|(,*)/g, "")) / 100);

const validateRange = (min: number, max: number) => {
  return async (_rule: any, value: string) => {
    const n = Number(value);
    if (n >= min && n <= max) {
      return;
    } else {
      throw new Error("invalid");
    }
  };
};

interface CustomerWarrantyManagementProps {
  open: boolean;
  onClose: () => void;
}

const CustomerWarrantyManagement: React.FC<CustomerWarrantyManagementProps> = ({
  open,
  onClose,
}) => {
  const configurator = useContext(ConfiguratorContext);
  const [warranties, setWarranties] = useState<CustomerWarrantyDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [editingWarranty, setEditingWarranty] = useState<CustomerWarrantyDto | null>(null);
  const [form] = useForm();
  const { data: customers } = useCustomers();

  const loadWarranties = async () => {
    try {
      setLoading(true);
      const response = await configurator.api.getCustomerWarranties();
      setWarranties(response.data);
    } catch (error) {
      message.error("Failed to load customer warranties");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadWarranties();
    }
  }, [open]);

  const handleAdd = () => {
    setEditingWarranty(null);
    form.resetFields();
    setEditModalOpen(true);
  };

  const handleEdit = (warranty: CustomerWarrantyDto) => {
    setEditingWarranty(warranty);
    form.setFieldsValue({
      ...warranty,
      customerIds: warranty.customers?.map((c) => c.id) || [],
    });
    setEditModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await configurator.api.deleteCustomerWarranty(id);
      message.success("Customer warranty deleted successfully");
      loadWarranties();
    } catch (error) {
      message.error("Failed to delete customer warranty");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      const warrantyData: CustomerWarrantyDto = {
        ...values,
        id: editingWarranty?.id,
      };

      if (editingWarranty) {
        await configurator.api.updateCustomerWarranty(warrantyData);
        message.success("Customer warranty updated successfully");
      } else {
        await configurator.api.createCustomerWarranty(warrantyData);
        message.success("Customer warranty created successfully");
      }

      setEditModalOpen(false);
      loadWarranties();
    } catch (error) {
      message.error("Failed to save customer warranty");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "ICE Fixed",
      dataIndex: "iceWarrantyFixed",
      key: "iceWarrantyFixed",
      render: (value: number) =>
        value != null ? `$${value.toLocaleString()}` : "-",
    },
    {
      title: "ICE %",
      dataIndex: "iceWarrantyPercentage",
      key: "iceWarrantyPercentage",
      render: (value: number) =>
        value != null ? `${(value * 100).toFixed(2)}%` : "-",
    },
    {
      title: "BEV Fixed",
      dataIndex: "bevWarrantyFixed",
      key: "bevWarrantyFixed",
      render: (value: number) =>
        value != null ? `$${value.toLocaleString()}` : "-",
    },
    {
      title: "BEV %",
      dataIndex: "bevWarrantyPercentage",
      key: "bevWarrantyPercentage",
      render: (value: number) =>
        value != null ? `${(value * 100).toFixed(2)}%` : "-",
    },
    {
      title: "Customers",
      dataIndex: "customers",
      key: "customers",
      render: (customers: any[]) =>
        customers?.map((c) => c.name).join(", ") || "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: CustomerWarrantyDto) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this warranty?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="Customer Warranty Management"
        open={open}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button key="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Warranty
          </Button>,
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
      >
        <Table
          dataSource={warranties}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Modal>

      <Modal
        title={editingWarranty ? "Edit Customer Warranty" : "Add Customer Warranty"}
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          onFinish={handleSave}
          layout="vertical"
          initialValues={{
            iceWarrantyFixed: 0,
            iceWarrantyPercentage: 0,
            bevWarrantyFixed: 0,
            bevWarrantyPercentage: 0,
          }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="Enter warranty name" />
          </Form.Item>

          <Form.Item
            label="Customers"
            name="customerIds"
            rules={[{ required: true, message: "At least one customer is required" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select customers"
              options={customers?.map((c) => ({
                label: `${c.name}${c.city ? ` - ${c.city}` : ''}${c.contactName ? ` (${c.contactName})` : ''} [ID: ${c.id}]`,
                value: c.id
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="ICE Warranty Fixed Cost"
            name="iceWarrantyFixed"
            rules={[
              { required: false },
              {
                type: "number",
                validator: validateRange(0, 100000),
                message: "Must be between 0 and 100,000",
              },
            ]}
          >
            <InputNumber
              formatter={moneyInputFormatter}
              parser={moneyInputParser}
              placeholder="Type dollar amount"
              controls={false}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="ICE Warranty Percentage of Material Cost"
            name="iceWarrantyPercentage"
            rules={[
              { required: false },
              {
                type: "number",
                validator: validateRange(0, 1.0),
                message: "Must be between 0 and 1.0",
              },
            ]}
          >
            <InputNumber
              formatter={percentInputFormatter}
              parser={percentInputParser}
              placeholder="Type percent"
              controls={false}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="BEV Warranty Fixed Cost"
            name="bevWarrantyFixed"
            rules={[
              { required: false },
              {
                type: "number",
                validator: validateRange(0, 100000),
                message: "Must be between 0 and 100,000",
              },
            ]}
          >
            <InputNumber
              formatter={moneyInputFormatter}
              parser={moneyInputParser}
              placeholder="Type dollar amount"
              controls={false}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="BEV Warranty Percentage of Material Cost"
            name="bevWarrantyPercentage"
            rules={[
              { required: false },
              {
                type: "number",
                validator: validateRange(0, 1.0),
                message: "Must be between 0 and 1.0",
              },
            ]}
          >
            <InputNumber
              formatter={percentInputFormatter}
              parser={percentInputParser}
              placeholder="Type percent"
              controls={false}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomerWarrantyManagement;
